import { createFileRoute, Link } from "@tanstack/react-router";
import { Film, MapPin, Users, Palette, Ticket, Newspaper, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({
    meta: [
      { title: "Admin — Overzicht" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminDashboard,
});

type Card = {
  title: string;
  description: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  accent?: boolean;
};

const contentCards: Card[] = [
  {
    title: "Films",
    description: "Voeg films toe, wijzig posters, badges, trailers en tickets per film.",
    to: "/admin/films",
    icon: Film,
    accent: true,
  },
  {
    title: "Locatie",
    description: "Tekst, plattegrond en sfeerfoto's van Stadspark Berg en Bos.",
    to: "/admin/locatie",
    icon: MapPin,
  },
  {
    title: "Partners",
    description: "Beheer sponsors, tiers, logo's en de sponsormap-PDF.",
    to: "/admin/sponsors",
    icon: Users,
  },
];

const settingsCards: Card[] = [
  {
    title: "Huisstijl",
    description: "Logo, favicon, kleuren, sterrenhemel en Google-tag.",
    to: "/admin/branding",
    icon: Palette,
  },
];

const externalLinks = [
  { title: "Homepage", to: "/", icon: ArrowRight },
  { title: "Ticketpagina", to: "/tickets", icon: Ticket },
  { title: "Pers-inschrijvingen", to: "/pers", icon: Newspaper },
];

function CardTile({ card }: { card: Card }) {
  return (
    <Link
      to={card.to}
      className={`group flex flex-col justify-between gap-6 rounded-2xl border p-6 transition-colors ${
        card.accent
          ? "border-gold/40 bg-gradient-to-br from-night-deep/70 to-night/40 hover:border-gold/70"
          : "border-border bg-night-deep/50 hover:border-gold/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold">
          <card.icon className="h-5 w-5" />
        </div>
        <h2 className="font-display text-xl">{card.title}</h2>
      </div>
      <p className="text-sm leading-relaxed text-foreground/70">{card.description}</p>
      <span className="inline-flex items-center gap-1.5 font-mono-cinema text-[10px] uppercase tracking-[0.18em] text-gold transition-transform group-hover:translate-x-0.5">
        Openen <ArrowRight className="h-3 w-3" />
      </span>
    </Link>
  );
}

function AdminDashboard() {
  return (
    <div className="px-5 py-10 md:px-10 md:py-14">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">— Overzicht</p>
            <h1 className="mt-3 font-display text-3xl font-medium md:text-4xl">Welkom terug</h1>
            <p className="mt-2 max-w-xl text-sm text-foreground/70">
              Beheer alles van de festivalwebsite op één plek: inhoud, huisstijl en de programmering.
              Gebruik het menu links om snel te navigeren.
            </p>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold/80">Inhoud</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {contentCards.map((c) => (
              <CardTile key={c.to} card={c} />
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold/80">Instellingen</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {settingsCards.map((c) => (
              <CardTile key={c.to} card={c} />
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold/80">Snel naar de site</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-3">
            {externalLinks.map((l) => (
              <li key={l.to}>
                <a
                  href={l.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between rounded-xl border border-border bg-night-deep/40 px-4 py-3 text-sm text-foreground/80 hover:border-gold/40 hover:text-foreground"
                >
                  {l.title}
                  <l.icon className="h-4 w-4 text-gold/70" />
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}